import React, { useState, useRef } from 'react';
import { Modal, Button, Checkbox, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useAccepTermsMutation, useLogoutMutation } from "../slices/usersApiSlice";
import { setLogin, setLogout } from "../redux/authSlice";
import { useNavigate } from 'react-router-dom';
import notify from "../utils/notifications";
import { 
  AiOutlineFileProtect, 
  AiOutlineCheckCircle, 
  AiOutlineCloseCircle, 
  AiOutlineSafetyCertificate, 
  AiOutlineBank, 
  AiOutlineLock,
  AiOutlineWarning
} from 'react-icons/ai';
import logo from "../assets/logo/TheQucikPayMe.png";
import "../styles/TermsAndConditions.css";

export default function TermsConditions() {
  const { userInfo } = useSelector(state => state.auth);

  const dispatch = useDispatch();
  const [accepTerms, { isLoading: isAccepting }] = useAccepTermsMutation();
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const navigate = useNavigate();
  
  const [checkedConsent, setCheckedConsent] = useState(false);
  const [highlightCheckbox, setHighlightCheckbox] = useState(false);
  const bodyRef = useRef(null);

  const hasAccepted = 
    userInfo?.termsAccepted === true || 
    userInfo?.termsAccepted === "true" || 
    userInfo?.termsAccepted === "1" || 
    userInfo?.termsAccepted === 1;

  if (!userInfo || hasAccepted) {
    return null;
  }

  const handleAcceptClick = async () => {
    if (!checkedConsent) {
      setHighlightCheckbox(true);
      notify.warning("Agreement Required", "Please check the agreement box below to accept Terms & Conditions.");
      
      // Scroll to bottom so checkbox is in full view
      if (bodyRef.current) {
        bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
      }

      setTimeout(() => {
        setHighlightCheckbox(false);
      }, 3500);
      return;
    }

    try {
      await accepTerms().unwrap();
      notify.success("Terms & Conditions Accepted", "Welcome to QuickPayMe platform.");
      
      // Allow toast to display smoothly before hiding modal
      setTimeout(() => {
        dispatch(setLogin({ ...userInfo, termsAccepted: true }));
      }, 400);
    } catch (err) {
      notify.error(err?.data?.message || "Failed to accept terms");
    }
  };

  const handleDecline = async () => {
    try {
      await logout().unwrap();
      notify.info("Logged Out", "Terms of Service declined.");
      dispatch(setLogout());
      navigate("/");
    } catch (err) {
      notify.error(err?.data?.message || err.error || "Failed to logout");
    }
  };

  return (
    <Modal
      open={!hasAccepted}
      footer={null}
      closable={false}
      centered
      width={780}
      className="terms-modal-wrapper"
    >
      <div className="terms-modal-container">
        {/* Header */}
        <div className="terms-modal-header">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <img src={logo} alt="QuickPayMe" style={{ height: "36px", objectFit: "contain" }} />
              <span className="terms-modal-divider"></span>
              <div>
                <h4 className="terms-modal-title">Terms & Conditions of Service</h4>
                <p className="terms-modal-subtitle">Please review and accept our platform usage terms to continue</p>
              </div>
            </div>
            <Tag color="cyan" style={{ fontWeight: 600, padding: "4px 10px", borderRadius: "6px" }}>
              <AiOutlineSafetyCertificate style={{ marginRight: 4 }} /> v1.2 Policy
            </Tag>
          </div>
        </div>

        {/* Scrollable Content Body */}
        <div className="terms-modal-body" ref={bodyRef}>
          {/* Notice Banner */}
          <div className="terms-banner-box">
            <AiOutlineFileProtect className="terms-banner-icon" />
            <div>
              <div className="terms-banner-title">Legal Agreement & Intermediary Policy</div>
              <div className="terms-banner-desc">
                This document is an electronic record generated under the Information Technology Act, 2000 and rules thereunder.
                Published in accordance with Rule 3 (1) of the IT (Intermediaries guidelines) Rules, 2011.
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div className="terms-card-section">
            <div className="terms-card-header">
              <AiOutlineLock className="terms-card-icon" />
              <span>1. Account Registration & Security</span>
            </div>
            <ul className="terms-styled-list">
              <li>You agree to provide true, accurate, and complete information during registration and keep your profile updated.</li>
              <li>You are solely responsible for maintaining the confidentiality of your login credentials and PIN/OTP code.</li>
              <li>Notify QuickPayMe support immediately if you suspect unauthorized access or compromise of your account.</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="terms-card-section">
            <div className="terms-card-header">
              <AiOutlineBank className="terms-card-icon" />
              <span>2. Financial Services & Wallet Transfers</span>
            </div>
            <ul className="terms-styled-list">
              <li>The Platform facilitates digital wallet loading, DMT, payout settlements, bill payments, and financial services.</li>
              <li>All transactions initiated through your authenticated session are final, non-reversible, and legally binding.</li>
              <li>Transactions are subject to third-party bank, NPCI, and payment gateway network processing rules.</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="terms-card-section">
            <div className="terms-card-header">
              <AiOutlineSafetyCertificate className="terms-card-icon" />
              <span>3. Refunds, Settlement & Dispute Resolution</span>
            </div>
            <ul className="terms-styled-list">
              <li>Refunds will be processed strictly for verified technical processing failures attributable to the Platform.</li>
              <li>Any transaction dispute must be reported to support within 7 business days of occurrence with reference IDs.</li>
              <li>Applicable service charges, MDR, and statutory taxes are displayed prior to transaction execution.</li>
            </ul>
          </div>

          {/* Section 4 */}
          <div className="terms-card-section">
            <div className="terms-card-header">
              <AiOutlineFileProtect className="terms-card-icon" />
              <span>4. Compliance & Governing Jurisdiction</span>
            </div>
            <ul className="terms-styled-list">
              <li>You agree to use the Platform exclusively for legitimate, lawful business activities and comply with Anti-Money Laundering (AML) norms.</li>
              <li>These Terms shall be governed by the laws of India, with exclusive jurisdiction in Hyderabad, Telangana.</li>
            </ul>
          </div>

          {/* Consent Checkbox Box with Pulsating Highlight */}
          <div className={`terms-consent-box ${highlightCheckbox ? "highlight-pulse" : ""}`}>
            <Checkbox 
              checked={checkedConsent} 
              onChange={e => {
                setCheckedConsent(e.target.checked);
                if (e.target.checked) setHighlightCheckbox(false);
              }}
            >
              <span className={`terms-consent-label ${highlightCheckbox ? "text-danger-highlight" : ""}`}>
                I have read, understood, and agree to abide by the QuickPayMe Terms of Service & Privacy Policy.
              </span>
            </Checkbox>
            {highlightCheckbox && (
              <div className="terms-highlight-hint">
                <AiOutlineWarning className="me-1" /> Check this box to enable Accept & Continue
              </div>
            )}
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="terms-modal-footer">
          <Button
            type="default"
            danger
            icon={<AiOutlineCloseCircle />}
            onClick={handleDecline}
            loading={isLoggingOut}
            className="terms-btn-decline"
          >
            Decline & Logout
          </Button>
          <Button
            type="primary"
            icon={<AiOutlineCheckCircle />}
            onClick={handleAcceptClick}
            loading={isAccepting}
            className={`terms-btn-accept ${!checkedConsent ? "btn-needs-consent" : ""}`}
          >
            Accept & Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}
